import { Switch } from '@/components/ui/switch';
import { Slider } from '@/components/ui/slider';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import type { LateralizationMapSettings as MapSettings, MapArrowScale, MapColorTheme } from '@/components/sop-intake/sop-analyzer/BodyDiagram';

interface LateralizationMapSettingsProps {
  settings: MapSettings;
  onChange: (settings: MapSettings) => void;
}

const setField = <K extends keyof MapSettings>(
  settings: MapSettings,
  onChange: (settings: MapSettings) => void,
  key: K,
  value: MapSettings[K]
) => {
  onChange({ ...settings, [key]: value });
};

export default function LateralizationMapSettings({ settings, onChange }: LateralizationMapSettingsProps) {
  return (
    <div className="w-[320px] space-y-4 rounded-lg border border-primary/40 bg-card p-4 text-foreground">
      <div>
        <h4 className="text-sm font-semibold">Lateralization Map Settings</h4>
        <p className="text-xs text-muted-foreground">View preferences for body-map overlays.</p>
      </div>

      <div className="flex items-center justify-between">
        <span className="text-xs">Show limb arrows</span>
        <Switch
          checked={settings.showLimbArrows}
          onCheckedChange={(checked) => setField(settings, onChange, 'showLimbArrows', Boolean(checked))}
        />
      </div>

      <div className="space-y-2">
        <span className="text-xs">Arrow size</span>
        <RadioGroup
          value={settings.arrowScale}
          onValueChange={(value) => setField(settings, onChange, 'arrowScale', value as MapArrowScale)}
          className="grid grid-cols-3 gap-2"
        >
          {(['S', 'M', 'L'] as const).map((size) => (
            <label
              key={size}
              className="flex cursor-pointer items-center justify-center gap-2 rounded-md border border-primary/20 bg-card/60 px-2 py-2 text-xs"
            >
              <RadioGroupItem value={size} />
              {size}
            </label>
          ))}
        </RadioGroup>
      </div>

      <div className="space-y-2">
        <span className="text-xs">Arrow color theme</span>
        <RadioGroup
          value={settings.colorTheme}
          onValueChange={(value) => setField(settings, onChange, 'colorTheme', value as MapColorTheme)}
          className="grid gap-2"
        >
          <label className="flex cursor-pointer items-center gap-2 rounded-md border border-primary/20 bg-card/60 px-3 py-2 text-xs">
            <RadioGroupItem value="default" />
            Default
          </label>
          <label className="flex cursor-pointer items-center gap-2 rounded-md border border-primary/20 bg-card/60 px-3 py-2 text-xs">
            <RadioGroupItem value="high-contrast" />
            High Contrast
          </label>
          <label className="flex cursor-pointer items-center gap-2 rounded-md border border-primary/20 bg-card/60 px-3 py-2 text-xs">
            <RadioGroupItem value="colorblind" />
            Colorblind-friendly
          </label>
        </RadioGroup>
      </div>

      <div className="flex items-center justify-between">
        <span className="text-xs">Show section labels</span>
        <Switch
          checked={settings.showSectionLabels}
          onCheckedChange={(checked) => setField(settings, onChange, 'showSectionLabels', Boolean(checked))}
        />
      </div>

      <div className="flex items-center justify-between">
        <span className="text-xs">Show legend</span>
        <Switch
          checked={settings.showLegend}
          onCheckedChange={(checked) => setField(settings, onChange, 'showLegend', Boolean(checked))}
        />
      </div>

      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <span className="text-xs">Background opacity</span>
          <span className="text-xs text-muted-foreground">{settings.backgroundOpacity.toFixed(2)}</span>
        </div>
        <Slider
          min={0.3}
          max={1}
          step={0.05}
          value={[settings.backgroundOpacity]}
          onValueChange={(value) => setField(settings, onChange, 'backgroundOpacity', Number(value[0] ?? 0.85))}
        />
      </div>
    </div>
  );
}
